import * as RadixSelect from '@radix-ui/react-select';
import { FC, forwardRef, Fragment, ReactElement, ReactNode } from 'react';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { FaIcon } from './FaIcon';
import { cn } from '../utils';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';

export type SelectItem =
  | {
      icon?: IconDefinition;
      value: string;
      label: string;
    }
  | {
      icon?: IconDefinition;
      value: string;
      label: string;
    }[];

type SelectProps = RadixSelect.SelectProps & {
  items: SelectItem[];
  onSelect?: (value: string) => void;
  container: HTMLElement;
  trigger: ReactElement;
  contentClassName?: string;
  itemClassName?: string;
  contentSettings?: RadixSelect.SelectContentProps;
};

export const Select: FC<SelectProps> = ({
  items,
  onSelect,
  container,
  trigger,
  contentClassName,
  defaultValue,
  itemClassName,
  contentSettings,
  ...rest
}) => {
  return (
    <RadixSelect.Root
      defaultValue={defaultValue}
      onValueChange={(value) => {
        onSelect?.(value);
      }}
      {...rest}
    >
      <RadixSelect.Trigger asChild>{trigger}</RadixSelect.Trigger>
      <RadixSelect.Portal container={container}>
        <RadixSelect.Content
          position="popper"
          avoidCollisions
          side="bottom"
          align="end"
          collisionPadding={12}
          {...(contentSettings || {})}
          className={cn(
            'lf:flex lf:min-w-[var(--radix-select-trigger-width)] lf:flex-col lf:gap-y-2 lf:rounded-xl lf:bg-neutral-0 lf:shadow-100',
            contentClassName,
          )}
        >
          <RadixSelect.Viewport className="lf:p-2">
            {items.map((item) => {
              if (Array.isArray(item)) {
                return (
                  <Fragment key={`fragment-${item[0].value}}`}>
                    <RadixSelect.Separator className="lf:my-1 lf:h-px lf:bg-neutral-100" />
                    <RadixSelect.Group key={`group-${item[0].value}`}>
                      {item.map(({ value, label }) => (
                        <SelectItem
                          className={itemClassName}
                          key={value}
                          value={value}
                        >
                          {label}
                        </SelectItem>
                      ))}
                    </RadixSelect.Group>
                  </Fragment>
                );
              }

              return (
                <SelectItem
                  className={itemClassName}
                  key={item.value}
                  value={item.value}
                  icon={item.icon}
                >
                  {item.label}
                </SelectItem>
              );
            })}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
};

const SelectItem = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
    className?: string;
    icon?: IconDefinition;
    value: string;
  }
>(({ children, className, icon, ...props }, forwardedRef) => {
  return (
    <RadixSelect.Item
      className={cn(
        'lf:flex lf:flex-1 lf:cursor-pointer lf:items-center lf:justify-start lf:gap-3 lf:rounded-lg lf:px-4 lf:py-2.5 lf:text-left lf:text-title-sm  lf:text-neutral-500 lf:hover:bg-neutral-200 lf:hover:outline-hidden lf:data-[state=checked]:text-prominence-500',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      {icon && <FaIcon icon={icon} />}
      <RadixSelect.ItemText asChild>
        <p>{children}</p>
      </RadixSelect.ItemText>
      <RadixSelect.ItemIndicator>
        <FaIcon
          className="lf:text-prominence-500"
          width={16}
          height={16}
          icon={faCheck}
        />
      </RadixSelect.ItemIndicator>
    </RadixSelect.Item>
  );
});

SelectItem.displayName = 'SelectItem';
